import React, { useMemo, useState } from 'react';
import { Edit3, MessageCircle, Trash2 } from 'lucide-react';
import { ThreadSummary } from '../../types/threads';
import { fuzzyFilter, groupThreads } from '../../lib/threads';
import { Doodle } from '../paper';

/**
 * The conversations tray (mobile's ConversationTray): a big "New chat",
 * search, and past conversations grouped by when they were last active.
 */
export const ConversationTray: React.FC<{
  threads: ThreadSummary[];
  currentThreadId: number | null;
  onSelect: (id: number) => void;
  onNew: () => void;
  onDelete: (id: number) => void;
  creating?: boolean;
}> = ({ threads, currentThreadId, onSelect, onNew, onDelete, creating }) => {
  const [query, setQuery] = useState('');
  const sections = useMemo(
    () => groupThreads(fuzzyFilter(threads, query, (t) => t.title ?? 'Untitled'), new Date()),
    [threads, query],
  );

  return (
    <div className="flex h-full flex-col px-4 pt-[calc(1rem+env(safe-area-inset-top,0px))] lg:pt-8">
      <h2 className="pb-heading-2 m-0">Conversations</h2>
      <button type="button" className="pb-btn pb-btn--primary mt-4 w-full py-3" onClick={onNew} disabled={creating}>
        <Edit3 size={16} /> New chat
      </button>
      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search conversations…"
        className="mt-3 w-full rounded-xl border border-solid border-outline-variant bg-surface-container-lowest px-3.5 py-2.5 font-body text-sm text-on-surface outline-none placeholder:text-on-surface-variant focus:border-accent focus:shadow-[0_0_0_3px_var(--focus-ring)]"
      />

      <div className="scrollbar-thin -mx-1 mt-1 flex-1 overflow-y-auto px-1 pb-6">
        {sections.length === 0 ? (
          <div className="mt-8 flex flex-col items-center gap-3 text-center">
            <Doodle kind="chat" size={0.6} />
            <p className="m-0 font-body text-[0.8125rem] text-on-surface-variant">
              {query ? 'Nothing matches that.' : 'No conversations yet. Ask your notebook something!'}
            </p>
          </div>
        ) : (
          sections.map((section) => (
            <div key={section.label}>
              <p className="m-0 mb-1.5 mt-5 px-2 font-body text-[10.5px] font-bold uppercase tracking-[0.17em] text-on-surface-variant">
                {section.label}
              </p>
              {section.threads.map((t) => {
                const active = t.id === currentThreadId;
                const title = t.title?.trim() || 'Untitled conversation';
                return (
                  <div
                    key={t.id}
                    className={`group flex min-h-[46px] items-center gap-2.5 rounded-xl px-2.5 ${
                      active ? 'bg-hl-lilac text-hl-lilac-ink' : 'text-on-surface hover:bg-surface-container-low'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => onSelect(t.id)}
                      aria-current={active ? 'true' : undefined}
                      className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 border-none bg-transparent p-0 py-3 text-left text-inherit"
                    >
                      <MessageCircle size={15} className={active ? '' : 'text-on-surface-variant'} />
                      <span className={`truncate font-body text-sm ${active ? 'font-semibold' : ''}`}>{title}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm(`Delete this conversation? “${title}” will be gone for good.`)) onDelete(t.id);
                      }}
                      aria-label={`Delete ${title}`}
                      className="flex cursor-pointer items-center border-none bg-transparent p-1 text-on-surface-variant opacity-0 transition-opacity hover:text-error focus-visible:opacity-100 group-hover:opacity-100 max-lg:opacity-100"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>
    </div>
  );
};
